"use client";
// beui.dev/components/motion/tabs

import { motion, MotionConfig, useReducedMotion } from "motion/react";
import { createContext, useCallback, useContext, useId, useMemo, useState } from "react";
import { EASE_OUT, SPRING_LAYOUT } from "lib/ease";
import { cn } from "lib/utils";

import "./tabs.css";

const TabsCtx = createContext(null);

function useTabs() {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error("Tabs.* must be used inside <Tabs>");
  return ctx;
}

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  variant = "pill",
  children,
  className,
}) {
  const [internal, setInternal] = useState(defaultValue ?? "");
  const [hoveredValue, setHoveredValue] = useState(null);
  const layoutId = useId();
  const reduce = useReducedMotion();
  const controlled = value !== undefined;
  const current = controlled ? value : internal;
  const setValue = useCallback((nextValue) => {
    if (!controlled) setInternal(nextValue);
    onValueChange?.(nextValue);
  }, [controlled, onValueChange]);
  const contextValue = useMemo(
    () => ({ value: current, setValue, layoutId, variant, hoveredValue, setHoveredValue }),
    [current, hoveredValue, layoutId, setValue, variant]
  );

  return (
    <MotionConfig transition={reduce ? { duration: 0 } : SPRING_LAYOUT}>
      <TabsCtx.Provider value={contextValue}>
        <motion.div layoutRoot className={cn("ui-tabs", `ui-tabs--${variant}`, className)}>
          {children}
        </motion.div>
      </TabsCtx.Provider>
    </MotionConfig>
  );
}

const listClasses = {
  pill: "inline-flex items-center gap-1 rounded-full bg-card p-1",
  underline: "inline-flex items-center gap-1 border-b border-border",
  segment: "inline-flex items-center gap-0 rounded-lg bg-card p-0.5",
};

export function TabsList({ children, className, wrapperClassName, ...props }) {
  const { variant } = useTabs();
  return (
    <div className={cn("ui-tabs-list-wrapper", wrapperClassName)}>
      <div
        role="tablist"
        className={cn("ui-tabs-list", `ui-tabs-list--${variant}`, listClasses[variant], className)}
        {...props}
      >
        {children}
      </div>
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
  className,
  indicatorClassName,
  hoverIndicatorClassName,
}) {
  const {
    value: current,
    setValue,
    layoutId,
    variant,
    hoveredValue,
    setHoveredValue,
  } = useTabs();
  const active = current === value;
  const hoverIndicatorEnabled = variant !== "segment";

  if (variant === "underline") {
    return (
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setValue(value)}
        className={cn(
          "ui-tabs-trigger ui-tabs-trigger--underline",
          active ? "ui-tabs-trigger--active" : "ui-tabs-trigger--inactive",
          className
        )}
      >
        {children}
        {active ? (
          <motion.span
            layoutId={layoutId}
            layout="position"
            className={cn("ui-tabs-indicator ui-tabs-indicator--underline", indicatorClassName)}
          />
        ) : null}
      </button>
    );
  }

  return (
    <div
      className={cn("ui-tabs-trigger-wrap", `ui-tabs-trigger-wrap--${variant}`)}
      onBlur={(event) => {
        if (hoverIndicatorEnabled && !event.currentTarget.contains(event.relatedTarget)) {
          setHoveredValue(null);
        }
      }}
      onFocus={() => hoverIndicatorEnabled && setHoveredValue(value)}
      onMouseEnter={() => hoverIndicatorEnabled && setHoveredValue(value)}
      onMouseLeave={() => hoverIndicatorEnabled && setHoveredValue(null)}
    >
      {hoverIndicatorEnabled && hoveredValue === value && !active ? (
        <motion.span
          layoutId={`${layoutId}-hover`}
          layout="position"
          className={cn("ui-tabs-hover-indicator", hoverIndicatorClassName)}
        />
      ) : null}
      {active ? (
        <motion.span
          layoutId={layoutId}
          layout={variant === "segment" ? true : "position"}
          style={{ borderRadius: variant === "pill" ? 9999 : 8 }}
          className={cn("ui-tabs-indicator", `ui-tabs-indicator--${variant}`, indicatorClassName)}
        />
      ) : null}
      <button
        type="button"
        role="tab"
        aria-selected={active}
        onClick={() => setValue(value)}
        className={cn(
          "ui-tabs-trigger",
          `ui-tabs-trigger--${variant}`,
          active ? "ui-tabs-trigger--active" : "ui-tabs-trigger--inactive",
          className
        )}
      >
        {children}
      </button>
    </div>
  );
}

export function TabsContent({ value, children, className }) {
  const { value: current } = useTabs();
  const reduce = useReducedMotion();
  const active = current === value;

  if (!active) {
    return (
      <div hidden className={className}>
        {children}
      </div>
    );
  }

  return (
    <motion.div
      key={value}
      initial={{ opacity: 0, y: reduce ? 0 : 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18, ease: EASE_OUT }}
      className={cn("mt-4", className)}
    >
      {children}
    </motion.div>
  );
}
